import { API_ROOT } from '$lib/apiUrls';
import { UnauthorizedError, ValidationError, type SvelteFetch, type ValidationErrorResponse } from '$lib/api/types';

export async function fetchJson<T, P>(
  url: string,
  method: RequestInit['method'],
  body: P,
  ignoreResponseBody = false,
  signal?: AbortSignal,
  svelteFetch?: SvelteFetch,
): Promise<T> {
  return await apiFetchRaw<T>(url, method, JSON.stringify(body), ignoreResponseBody, signal, svelteFetch);
}

export async function apiFetchRaw<T>(
  url: string,
  method: RequestInit['method'],
  body: RequestInit['body'],
  ignoreResponseBody = false,
  signal?: AbortSignal,
  svelteFetch?: SvelteFetch,
): Promise<T> {
  const fetchMethod = svelteFetch ?? fetch;

  const response = await fetchMethod(`${API_ROOT}${url}`, {
    method,
    headers:
      body instanceof FormData
        ? undefined
        : {
            'Content-Type': 'application/json',
          },
    body,
    signal,
  });

  if (response.status === 400) {
    let body: ValidationErrorResponse;
    try {
      body = await response.json();
    } catch {
      throw new Error('Unexpected 400 response from server.');
    }

    if (!body.errors) {
      throw new Error('Unexpected 400 response from server. Response does not have errors property.');
    }

    throw new ValidationError('One or more validation errors occurred.', body.errors);
  } else if (response.status === 401) {
    let body: unknown | undefined = undefined;
    if (response.headers.get('Content-Type')?.includes('application/json')) {
      body = await response.json();
    }
    throw new UnauthorizedError('Unauthorized to access this resource.', body);
  } else if (!response.ok) {
    throw new Error(`Unexpected response from server: ${response.status} ${response.statusText}`);
  }

  if (ignoreResponseBody) {
    return undefined as unknown as T;
  }

  return await response.json();
}

export async function getJson<T>(url: string, signal?: AbortSignal, svelteFetch?: SvelteFetch): Promise<T> {
  return await fetchJson(url, 'GET', undefined, false, signal, svelteFetch);
}
